// PRODUCTION — admin flow editor step group (collect-data steps config) settings
import React, { useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import type { BasicSelectOption } from 'src/components/shared/form/BasicSelect'
import { FLOW_EDITOR_FEATURE_FLAGS } from 'src/constants/flowEditorFeatureFlags'
import { ConfigRow } from './stepConfigForms/shared/layout'
import { SoloCheckbox } from './stepConfigForms/shared/SoloCheckbox'
import { flowEditorTextInputCss } from './flowEditorInputStyles'
import { ProtoSelectStandalone } from './ProtoSelectStandalone'

export interface FlowEditorStepGroupSettingsPanelProps {
  canMoveDown: boolean
  canMoveUp: boolean
  canRemoveGroup: boolean
  delayed: boolean
  groupOrdinal: number
  insurancePolicyId: string | null
  insuranceSyncStepsError?: string | null
  insurancePolicyOptions: BasicSelectOption[]
  insurancePolicyReadOnly: boolean
  insuranceSyncStepsDisabled?: boolean
  label: string
  onDelayedChange: (delayed: boolean) => void
  onInsurancePolicyChange: (policyId: string | null) => void
  onSyncInsurancePolicySteps?: () => void
  onLabelChange: (label: string) => void
  onMoveGroup: (direction: 'up' | 'down') => void
  onRemoveGroup: () => void
  showInsurancePolicySelector?: boolean
  totalGroups: number
}

const reorderStepBtnCss = (enabled: boolean, edge: 'left' | 'right') => [
  tw`flex h-[30px] w-[30px] items-center justify-center border-0 bg-transparent transition-colors duration-150 border-solid`,
  edge === 'left'
    ? tw`border-r border-shades-200 border-solid`
    : tw`border-l border-shades-200 border-solid`,
  enabled
    ? tw`cursor-pointer text-shades-600 hover:bg-shades-50 hover:text-shades-900`
    : tw`cursor-not-allowed text-shades-300`,
]

export function FlowEditorStepGroupSettingsPanel({
  canMoveDown,
  canMoveUp,
  canRemoveGroup,
  delayed,
  groupOrdinal,
  insurancePolicyId,
  insuranceSyncStepsError,
  insurancePolicyOptions,
  insurancePolicyReadOnly,
  insuranceSyncStepsDisabled = false,
  label,
  onDelayedChange,
  onInsurancePolicyChange,
  onLabelChange,
  onMoveGroup,
  onRemoveGroup,
  onSyncInsurancePolicySteps,
  showInsurancePolicySelector = true,
  totalGroups,
}: FlowEditorStepGroupSettingsPanelProps) {
  const [pendingRemove, setPendingRemove] = useState(false)
  const allowReorder = FLOW_EDITOR_FEATURE_FLAGS.allowReorderSteps

  const insurancePolicyOptionsWithNone = useMemo(
    (): BasicSelectOption[] => [{ label: 'None', value: '' }, ...insurancePolicyOptions],
    [insurancePolicyOptions],
  )

  const titleText = label.trim() !== '' ? label : 'Step group'

  return (
    <div css={tw`flex flex-col rounded-xl border border-shades-200 bg-shades-0 border-solid`}>
      <div css={tw`flex items-start gap-2.5 border-b border-shades-100 px-5 py-3.5 border-solid`}>
        <div css={tw`flex min-w-0 flex-1 flex-col gap-1.5`}>
          <span
            css={tw`min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[15px] font-semibold text-shades-900`}
            title={titleText}
          >
            {titleText}
          </span>
        </div>

        <div css={tw`flex flex-shrink-0 items-center gap-2`}>
          {allowReorder ? (
            <div
              css={tw`flex flex-shrink-0 items-center gap-0.5 overflow-hidden rounded-lg border border-shades-200 border-solid`}
            >
              <button
                type="button"
                onClick={() => onMoveGroup('up')}
                disabled={!canMoveUp}
                css={reorderStepBtnCss(canMoveUp, 'left')}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="18 15 12 9 6 15" />
                </svg>
              </button>
              <span
                css={tw`min-w-[44px] select-none text-center text-xs font-semibold text-shades-600`}
              >
                {groupOrdinal} / {totalGroups}
              </span>
              <button
                type="button"
                onClick={() => onMoveGroup('down')}
                disabled={!canMoveDown}
                css={reorderStepBtnCss(canMoveDown, 'right')}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            </div>
          ) : null}

          {canRemoveGroup ? (
            pendingRemove ? (
              <div css={tw`flex flex-shrink-0 items-center gap-1.5`}>
                <span css={tw`whitespace-nowrap text-[13px] font-medium text-shades-500`}>
                  Delete group?
                </span>
                <Button
                  variant="outlined"
                  size="tiny"
                  color="white"
                  onClick={() => setPendingRemove(false)}
                  className={tw`!border-shades-200 !text-shades-700 hover:!bg-shades-100`}
                >
                  Cancel
                </Button>
                <Button
                  size="tiny"
                  color="primary"
                  onClick={() => {
                    setPendingRemove(false)
                    onRemoveGroup()
                  }}
                  className={tw`!bg-critical-500 hover:!bg-critical-600 active:!bg-critical-600`}
                >
                  Delete
                </Button>
              </div>
            ) : (
              <Button
                size="small"
                color="grey"
                onClick={() => setPendingRemove(true)}
                leftIcon={
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="red"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    <line x1="10" y1="11" x2="10" y2="17" />
                    <line x1="14" y1="11" x2="14" y2="17" />
                  </svg>
                }
              />
            )
          ) : null}
        </div>
      </div>

      <ConfigRow label="Name">
        <input
          id="flow-editor-step-group-name"
          type="text"
          value={label}
          onChange={(e) => onLabelChange(e.target.value)}
          css={flowEditorTextInputCss}
        />
      </ConfigRow>

      {showInsurancePolicySelector ? (
        <ConfigRow label="Insurance policy" disabled={insurancePolicyReadOnly}>
          <div css={tw`flex min-w-0 flex-1 flex-col gap-2`}>
            <div css={tw`flex flex-wrap items-center gap-2`}>
              <ProtoSelectStandalone
                value={
                  insurancePolicyId !== null && insurancePolicyId.trim() !== ''
                    ? insurancePolicyId.trim()
                    : ''
                }
                onValueChange={(v) => {
                  const s = String(v).trim()

                  onInsurancePolicyChange(s === '' ? null : s)
                }}
                options={insurancePolicyOptionsWithNone}
                placeholder="None"
                disabled={insurancePolicyReadOnly}
              />
              {onSyncInsurancePolicySteps ? (
                <Button
                  variant="outlined"
                  size="small"
                  color="white"
                  title={
                    insuranceSyncStepsDisabled
                      ? 'Remove non-insurance steps from this group before syncing'
                      : 'Replace insurance steps in this group with steps from the selected policy'
                  }
                  onClick={onSyncInsurancePolicySteps}
                  disabled={insuranceSyncStepsDisabled}
                  className={tw`!h-[38px] !flex-shrink-0 !whitespace-nowrap !border-shades-200 !px-3.5 !text-[13px] !text-shades-700 hover:!border-shades-300 hover:!bg-shades-50`}
                >
                  Sync steps
                </Button>
              ) : null}
            </div>
            {insuranceSyncStepsError ? (
              <div role="alert" css={tw`text-[13px] font-medium leading-snug text-critical-500`}>
                {insuranceSyncStepsError}
              </div>
            ) : null}
          </div>
        </ConfigRow>
      ) : null}

      <ConfigRow label="Delayed">
        <div css={tw`flex min-w-0 flex-1 items-center`}>
          <label css={tw`flex cursor-pointer select-none items-center gap-2`}>
            <SoloCheckbox checked={delayed} onChange={onDelayedChange} />
            <span css={tw`text-sm font-medium text-shades-800`}>Applies to entire group</span>
          </label>
        </div>
      </ConfigRow>
    </div>
  )
}
